import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useConfirm } from '../hooks/useConfirm';

const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
const STATUS_CLASS = {
  pending: 'text-yellow-500',
  approved: 'text-win',
  rejected: 'text-destructive',
};

/**
 * Whether this account may author screener Python.
 *
 * Three states, because the superadmin's access is derived from
 * SUPERADMIN_EMAIL rather than the column — rendering that as an off switch
 * would misreport it. Accounts that are not approved yet cannot be granted it,
 * so the control explains itself instead of silently disappearing.
 */
function CodeAccess({ u, busy, onToggle }) {
  if (u.is_superadmin) {
    return (
      <span
        className="text-xs text-muted-foreground border border-border rounded px-2 py-1"
        title="Superadmin — code access comes from SUPERADMIN_EMAIL, not this flag"
      >
        Python: always
      </span>
    );
  }

  if (u.status !== 'approved') {
    return (
      <span
        className="text-xs text-muted-foreground/60 border border-dashed border-border rounded px-2 py-1"
        title="Approve this account first"
      >
        Python: —
      </span>
    );
  }

  return (
    <Button
      size="sm"
      variant={u.code_enabled ? 'secondary' : 'outline'}
      disabled={busy}
      onClick={onToggle}
      title={u.code_enabled
        ? 'Can author screener Python — click to revoke'
        : 'Spec builder only — click to allow Python'}
    >
      {u.code_enabled ? 'Python: on' : 'Python: off'}
    </Button>
  );
}

export default function AdminApproval() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    setLoading(true);
    setError('');
    api.get(`/admin/users?status=${filter}`)
      .then((res) => setUsers(res.data))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, [filter]);

  const act = async (userId, action) => {
    setActionLoading(userId + action);
    try {
      const res = await api.post(`/admin/users/${userId}/${action}`);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: res.data.status } : u));
    } catch {
      setError(`Failed to ${action} user`);
    } finally {
      setActionLoading(null);
    }
  };

  // Screener Python is executed server-side with the backend's own environment
  // and database credentials, so granting it is a privileged act rather than a
  // preference — hence the confirmation. Everyone else builds screeners in the
  // spec builder, which can only express the attribute catalog.
  const toggleCode = async (u) => {
    const granting = !u.code_enabled;
    const ok = await confirm(
      granting
        ? `${u.name} will be able to write screener Python, which runs on the `
          + 'server with its database credentials. Grant it only to people you '
          + 'trust with that.'
        : `${u.name} will no longer be able to edit or create Python screeners. `
          + 'Their existing ones keep running, and they can still use the spec '
          + 'builder.',
      {
        title: granting ? 'Allow Python authoring?' : 'Revoke Python authoring?',
        confirmText: granting ? 'Allow' : 'Revoke',
        isDestructive: !granting,
      },
    );
    if (!ok) return;

    setActionLoading(u.id + 'code');
    setError('');
    try {
      const res = await api.post(`/admin/users/${u.id}/toggle-code`);
      setUsers((prev) => prev.map(
        (x) => (x.id === u.id ? { ...x, code_enabled: res.data.code_enabled } : x),
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change code access');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="max-w-3xl flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">User access</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Approve accounts, and choose who may author screeners in Python.
            </p>
          </div>
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected'].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  filter === s
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:border-foreground'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {confirmDialog}

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 gap-4">
                <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Skeleton className="h-4 w-14" />
                  <Skeleton className="h-7 w-20 rounded" />
                  <Skeleton className="h-7 w-20 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No {filter} users.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {users.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 gap-4"
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-sm font-medium truncate">{u.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                  <span className="text-xs text-muted-foreground">
                    Registered {new Date(u.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-medium ${STATUS_CLASS[u.status]}`}>
                    {STATUS_LABEL[u.status]}
                  </span>
                  <CodeAccess
                    u={u}
                    busy={actionLoading === u.id + 'code'}
                    onToggle={() => toggleCode(u)}
                  />
                  {u.status !== 'approved' && (
                    <Button
                      size="sm"
                      disabled={actionLoading === u.id + 'approve'}
                      onClick={() => act(u.id, 'approve')}
                    >
                      Approve
                    </Button>
                  )}
                  {u.status !== 'rejected' && (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={actionLoading === u.id + 'reject'}
                      onClick={() => act(u.id, 'reject')}
                    >
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
